import { ApplicationResourcesFormat } from '@/application/ApplicationResourcesFormat'
import GameSignals from '@/signals/GameSignals'
import { ModulesManager as modulesManager } from '@modules/src/managers/ModulesManager'
import { DeviceUtils } from '@modules/src/utils/DeviceUtils'
import { Assets } from 'pixi.js'
import * as WebFont from 'webfontloader'
import { buildAssetUrl } from '@/utils/AssetsUrlUtil'
import { loadGeneratedSpritesheets } from '@/assets/loadGeneratedSpritesheets'

export class ApplicationLoader {
    public static readonly SPINE_ATLASES_URL: string = buildAssetUrl('/assets/spritesheets/animations_{v}_{n1}.json')
    public static readonly DESKTOP_ATLASES_COUNT: number = 0
    public static readonly MOBILE_ATLASES_COUNT: number = 0
    private static readonly MAIN_ASSETS_WEIGHT = 0.8
    private static readonly FINAL_ASSETS_WEIGHT = 0.2
    private static readonly FINAL_LOADING_WEIGHTS = {
        spritesheets: 0.7,
        spineAtlases: 0.2,
        winline: 0.1,
    }

    private static emitOverallProgress(progress01: number): void {
        const normalized = Math.max(0, Math.min(1, Number(progress01) || 0))
        GameSignals.loadingProgress.emit(normalized * 100)
    }

    private static mapFinalPhaseProgress(progress01: number): number {
        const normalized = Math.max(0, Math.min(1, Number(progress01) || 0))

        return ApplicationLoader.MAIN_ASSETS_WEIGHT + normalized * ApplicationLoader.FINAL_ASSETS_WEIGHT
    }

    public static async loadPreloadingAssets() {
        try {
            await modulesManager.preloadAssets()
            this.onLoadPreloadingAssetsComplete()
        } catch (error) {
            console.error('Error loading preload assets:', error)
        }
    }

    private static onLoadPreloadingAssetsComplete = () => {
        GameSignals.preloaderAssetsLoadingComplete.emit()
    }

    public static async loadMainGameAssets() {
        try {
            this.emitOverallProgress(0)

            await modulesManager.loadMainAssets((progress) => {
                const normalized = Math.max(0, Math.min(1, (Number(progress) || 0) / 100))
                this.emitOverallProgress(normalized * ApplicationLoader.MAIN_ASSETS_WEIGHT)
            })

            this.emitOverallProgress(ApplicationLoader.MAIN_ASSETS_WEIGHT)

            // Continue with the final asset phase without resetting visible progress.
            this.emitOverallProgress(ApplicationLoader.MAIN_ASSETS_WEIGHT)

            await loadGeneratedSpritesheets((progress) => {
                const p = Math.max(0, Math.min(1, Number(progress) || 0))
                this.emitOverallProgress(
                    this.mapFinalPhaseProgress(p * ApplicationLoader.FINAL_LOADING_WEIGHTS.spritesheets)
                )
            })

            let finalProgress = ApplicationLoader.FINAL_LOADING_WEIGHTS.spritesheets
            this.emitOverallProgress(this.mapFinalPhaseProgress(finalProgress))

            if (ApplicationResourcesFormat.MERGED_SPINE_ATLASES_ENABLED) {
                const { SPINE_ATLASES_URL, DESKTOP_ATLASES_COUNT, MOBILE_ATLASES_COUNT } = ApplicationLoader
                const channelAtlasesCount = DeviceUtils.isMobileDevice() ? MOBILE_ATLASES_COUNT : DESKTOP_ATLASES_COUNT
                const channelType = DeviceUtils.isMobileDevice() ? 'mobile' : 'desktop'
                console.log('Loading spine atlases:', channelAtlasesCount)

                for (let index = 0; index < channelAtlasesCount; index++) {
                    const spineAtlasUrl = SPINE_ATLASES_URL.replace('{v}', channelType).replace(
                        '{n1}',
                        (index + 1).toString()
                    )
                    await Assets.load(spineAtlasUrl)

                    const atlasesProgress = channelAtlasesCount > 0 ? (index + 1) / channelAtlasesCount : 1
                    finalProgress =
                        ApplicationLoader.FINAL_LOADING_WEIGHTS.spritesheets +
                        atlasesProgress * ApplicationLoader.FINAL_LOADING_WEIGHTS.spineAtlases
                    this.emitOverallProgress(this.mapFinalPhaseProgress(finalProgress))
                }
            } else {
                finalProgress =
                    ApplicationLoader.FINAL_LOADING_WEIGHTS.spritesheets +
                    ApplicationLoader.FINAL_LOADING_WEIGHTS.spineAtlases
                this.emitOverallProgress(this.mapFinalPhaseProgress(finalProgress))
            }

            await ApplicationLoader.loadWinlineAssets()
            this.emitOverallProgress(1)
            this.onLoadMainGameAssetsComplete()
        } catch (error) {
            console.error('Error loading main game assets:', error)
        }
    }

    public static async loadWinlineAssets(): Promise<void> {
        const winlineUrl = buildAssetUrl('/assets/winline/winline.png')
        try {
            Assets.add({ alias: 'winline/winline', src: winlineUrl })
        } catch {
            // already registered
        }
        await Assets.load('winline/winline')
    }

    private static onLoadMainGameAssetsComplete = () => {
        GameSignals.mainGameAssetsLoadingComplete.emit()
    }

    public static async loadGameFonts(): Promise<void> {
        return new Promise((resolve, reject) => {
            const fontsUrl = buildAssetUrl('/assets/fonts/stylesheet.css')
            console.log('Loading fonts from:', fontsUrl)

            WebFont.load({
                active: () => {
                    console.log('Fonts loaded successfully')
                    resolve()
                },
                inactive: () => {
                    console.warn('Fonts loading inactive/timeout')
                    resolve() // Don't block if fonts fail
                },

                custom: {
                    families: ['Rubik_Game', 'Roboto-Black', 'Montserrat', 'Montserrat_Bold', 'MyriadPro-Black'],
                    urls: [fontsUrl],
                },
            })
        })
    }
}
